import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, must, supabase } from "./supabase.js";
import { acceptPendingInvitation } from "./invitations.js";
import type { ObligationRow, StatusRow, TaxpayerRow } from "./types.js";

export const qk = {
  orgs: ["orgs"] as const,
  taxpayers: (org: string) => ["taxpayers", org] as const,
  taxpayer: (id: string) => ["taxpayer", id] as const,
  obligations: (org: string) => ["obligations", org] as const,
  statuses: (org: string) => ["statuses", org] as const,
  snapshots: (tp: string) => ["snapshots", tp] as const,
  registered: (tp: string) => ["registered", tp] as const,
  documents: (tp: string) => ["documents", tp] as const,
  audit: (tp: string) => ["audit", tp] as const,
  definitions: ["definitions"] as const,
  prefs: ["prefs"] as const,
  notifications: ["notifications"] as const,
};

export function useOrgs() {
  return useQuery({
    queryKey: qk.orgs,
    queryFn: async () => {
      // An invitation link opened before signing in is accepted here (DES-74).
      await acceptPendingInvitation();
      return must(await supabase.from("organizations").select("id, name, created_at").order("created_at"));
    },
  });
}

export function useCreateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => must(await supabase.rpc("create_organization", { p_name: name })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.orgs }),
  });
}

export function useTaxpayers(org: string) {
  return useQuery({
    queryKey: qk.taxpayers(org),
    queryFn: async () =>
      must(await supabase.from("taxpayers").select("*").eq("organization_id", org).is("archived_at", null).order("created_at")) as TaxpayerRow[],
  });
}

export function useTaxpayer(id: string) {
  return useQuery({
    queryKey: qk.taxpayer(id),
    queryFn: async () => must(await supabase.from("taxpayers").select("*").eq("id", id).single()) as TaxpayerRow,
  });
}

export function useObligations(org: string) {
  return useQuery({
    queryKey: qk.obligations(org),
    queryFn: async () =>
      must(await supabase.from("obligation_status").select("*").eq("organization_id", org).order("due_date").limit(2000)) as ObligationRow[],
  });
}

export function useStatuses(org: string) {
  return useQuery({
    queryKey: qk.statuses(org),
    queryFn: async () => must(await supabase.from("taxpayer_latest_status").select("*").eq("organization_id", org)) as StatusRow[],
  });
}

export function useSnapshot(tp: string) {
  return useQuery({
    queryKey: qk.snapshots(tp),
    queryFn: async () => {
      const snap = must(
        await supabase.from("taxpayer_profile_snapshots").select("*").eq("taxpayer_id", tp).order("fetched_at", { ascending: false }).limit(1),
      ) as Record<string, unknown>[];
      const acts = snap[0]
        ? (must(await supabase.from("economic_activities").select("*").eq("snapshot_id", snap[0].id as string).order("kind")) as Record<string, unknown>[])
        : [];
      return { snapshot: snap[0] ?? null, activities: acts };
    },
  });
}

export function useRegistered(tp: string) {
  return useQuery({
    queryKey: qk.registered(tp),
    queryFn: async () => must(await supabase.from("registered_obligations").select("*").eq("taxpayer_id", tp).order("obligation_code")) as Record<string, string>[],
  });
}

export function useDefinitions() {
  return useQuery({
    queryKey: qk.definitions,
    staleTime: 3_600_000,
    queryFn: async () => must(await supabase.from("obligation_definitions").select("*").eq("active", true).order("sort_order")) as Record<string, unknown>[],
  });
}

export function usePenaltyParams() {
  return useQuery({
    queryKey: ["penalty-params"],
    staleTime: 3_600_000,
    queryFn: async () =>
      must(await supabase.from("parameter_versions").select("key, effective_from, effective_to, value").eq("review_status", "APPROVED")) as import("@despejo/compliance-engine").ParameterRow[],
  });
}

export function useDocuments(tp: string) {
  return useQuery({
    queryKey: qk.documents(tp),
    queryFn: async () => must(await supabase.from("documents").select("*").eq("taxpayer_id", tp).order("uploaded_at", { ascending: false })) as Record<string, string>[],
  });
}

export function useAudit(tp: string) {
  return useQuery({
    queryKey: qk.audit(tp),
    queryFn: async () =>
      must(await supabase.from("audit_log").select("id, at, action, entity, reason, actor").eq("taxpayer_id", tp).order("at", { ascending: false }).limit(200)) as Record<string, string>[],
  });
}

export function useInvalidateOrg(org: string) {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: qk.obligations(org) }),
      qc.invalidateQueries({ queryKey: qk.statuses(org) }),
      qc.invalidateQueries({ queryKey: qk.taxpayers(org) }),
      qc.invalidateQueries({ predicate: (q) => ["taxpayer", "snapshots", "registered", "documents", "audit"].includes(q.queryKey[0] as string) }),
    ]);
}

// ------------------------------------------------------------------ worker API

export interface CreateTaxpayerInput {
  organization_id: string;
  id_type: string;
  id_number: string;
  display_name?: string;
}

export const workerApi = {
  lookup: (id: string) => api<{ profile: import("@despejo/hacienda-client").TaxpayerProfile | null; error?: string }>(`/api/hacienda/lookup?id=${encodeURIComponent(id)}`),
  createTaxpayer: (input: CreateTaxpayerInput) => api<{ taxpayer: TaxpayerRow }>(`/api/taxpayers`, { method: "POST", body: JSON.stringify(input) }),
  refreshProfile: (taxpayerId: string) => api<{ changes: unknown[] }>(`/api/taxpayers/${taxpayerId}/refresh-profile`, { method: "POST" }),
  generate: (taxpayerId: string) => api<{ inserted: number; updated: number; superseded: number; issues: unknown[] }>(`/api/taxpayers/${taxpayerId}/generate`, { method: "POST" }),
};
